import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MessageSquare, CheckCircle2, XCircle, Hourglass, Loader2, Mail, AlertTriangle, Download } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useWebSocket } from "../hooks/useWebSocket";
import logging from "../utils/logging";
import { downloadCSV, stampedFilename } from "../utils/csvExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  cleared: "bg-green-100 text-green-800",
  upheld: "bg-red-100 text-red-800",
};

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminAppealsPage() {
  const { t } = useTranslation();
  const STATUS_FILTERS = [
    { value: "pending", label: t("appeals.filter_pending"), icon: Hourglass },
    { value: "cleared", label: t("appeals.filter_cleared"), icon: CheckCircle2 },
    { value: "upheld", label: t("appeals.filter_upheld"), icon: XCircle },
    { value: "all", label: t("appeals.filter_all"), icon: MessageSquare },
  ];
  const { user } = useAuth();
  const [appeals, setAppeals] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // appeal being reviewed
  const [adminNotes, setAdminNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const isAdmin = user && (user.role === "system_admin" || user.role === "govt_admin");

  const fetchAppeals = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/appeals`, {
        params: { status: statusFilter, limit: 100 },
        withCredentials: true,
      });
      setAppeals(r.data || []);
    } catch (err) {
      logging.error("Appeals", err);
      setAppeals([]);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { if (isAdmin) fetchAppeals(); }, [fetchAppeals, isAdmin]);

  // Live inbox: when a citizen submits a new appeal, refetch the list and
  // surface a small toast so the admin notices without leaving the page.
  // We dedupe by appeal_id within a short window because broadcast can
  // arrive twice on reconnect.
  const seenAppealIds = useRef(new Set());
  const handleWsMessage = useCallback((msg) => {
    if (msg?.type !== "appeal_submitted") return;
    if (msg.appeal_id && seenAppealIds.current.has(msg.appeal_id)) return;
    if (msg.appeal_id) seenAppealIds.current.add(msg.appeal_id);
    if (statusFilter === "pending" || statusFilter === "all") fetchAppeals();
    toast.message(t("appeals.new_from", { name: msg.user_name || t("appeals.new_from_default") }), {
      description: t("appeals.inbox_updated"),
    });
  }, [statusFilter, fetchAppeals, t]);
  useWebSocket(handleWsMessage);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/appeals/${active.appeal_id}`,
        { decision, admin_notes: adminNotes.trim() },
        { withCredentials: true },
      );
      toast.success(decision === "cleared" ? t("appeals.lifted_toast") : t("appeals.upheld_toast"));
      setActive(null);
      setAdminNotes("");
      fetchAppeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("appeals.decision_failed"));
    }
    setBusy(false);
  };

  const handleExport = useCallback(() => {
    if (!appeals.length) {
      toast.message(t("appeals.nothing_to_export"));
      return;
    }
    const n = downloadCSV({
      filename: stampedFilename(`guard_appeals_${statusFilter}`),
      columns: [
        { key: "appeal_id", header: "Appeal ID" },
        { key: "user_name", header: "User" },
        { key: "user_email", header: "Email" },
        { key: "status", header: "Status" },
        { key: "sanction_reason", header: "Sanction Reason" },
        { key: "message", header: "User Message" },
        { key: "admin_notes", header: "Admin Notes" },
        { key: "reviewed_by_name", header: "Reviewed By" },
        { key: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : ""), header: "Submitted" },
        { key: (r) => (r.reviewed_at ? new Date(r.reviewed_at).toLocaleString() : ""), header: "Reviewed" },
      ],
      data: appeals,
    });
    toast.success(t(n === 1 ? "appeals.exported_n_one" : "appeals.exported_n", { n }));
  }, [appeals, statusFilter, t]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F3F4F6]">
        <Header notifications={notifications} onNotificationRead={() => {}} />
        <div className="p-8 max-w-3xl mx-auto text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <p className="text-sm font-body text-gray-600">{t("appeals.admins_only")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-appeals-page">
      <Header notifications={notifications} onNotificationRead={() => setNotifications([])} />
      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 font-body mb-3" data-testid="appeals-back">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("appeals.back")}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="w-7 h-7 text-gray-900" strokeWidth={2} />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("appeals.title")}</h1>
            <p className="text-sm text-gray-500 font-body">{t("appeals.subtitle")}</p>
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="appeals-filters">
          {STATUS_FILTERS.map(({ value, label, icon: Icon }) => {
            const active = statusFilter === value;
            return (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`text-xs font-bold rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5 border transition ${active ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                data-testid={`appeals-filter-${value}`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={loading || !appeals.length}
            className="ml-auto rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50 text-xs font-bold"
            data-testid="appeals-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> {t("appeals.export_csv")}
          </Button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-sm text-gray-400 font-body">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> {t("appeals.loading")}
          </div>
        ) : appeals.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-sm text-gray-400 font-body" data-testid="appeals-empty">
            {statusFilter === "all" ? t("appeals.empty_all") : t("appeals.empty", { status: statusFilter })}
          </div>
        ) : (
          <div className="space-y-3">
            {appeals.map((a) => (
              <div key={a.appeal_id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`appeal-row-${a.appeal_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading text-base font-bold text-gray-900 truncate">{a.user_name || "Unknown"}</p>
                      <Badge className={`text-[10px] rounded-sm ${STATUS_BADGE[a.status] || ""}`}>{a.status}</Badge>
                    </div>
                    <p className="text-[11px] text-gray-500 font-body inline-flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {a.user_email}
                      {" · "}submitted {formatTime(a.created_at)}
                    </p>
                  </div>
                  {a.status === "pending" && (
                    <Button
                      onClick={() => { setActive(a); setAdminNotes(""); }}
                      size="sm"
                      className="bg-black hover:bg-gray-800 text-white rounded-sm shrink-0 text-xs"
                      data-testid={`appeal-review-${a.appeal_id}`}
                    >
                      {t("appeals.review")}
                    </Button>
                  )}
                </div>
                {a.sanction_reason && (
                  <p className="text-[11px] bg-red-50 border border-red-100 text-red-800 rounded-sm p-1.5 mb-2 font-body">
                    <strong className="font-bold">{t("appeals.sanction_reason")}</strong> {a.sanction_reason}
                  </p>
                )}
                <p className="text-sm text-gray-800 font-body whitespace-pre-wrap">{a.message}</p>
                {a.status !== "pending" && (
                  <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 font-body">
                    {a.status === "cleared" ? t("appeals.filter_cleared") : t("appeals.filter_upheld")} by <strong>{a.reviewed_by_name || "admin"}</strong> {formatTime(a.reviewed_at)}
                    {a.admin_notes && <p className="mt-1 text-gray-700">{t("appeals.admin_notes_label")} {a.admin_notes}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-lg" data-testid="appeal-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter">{t("appeals.decision_title")}</DialogTitle>
            <DialogDescription>
              {t("appeals.decision_desc")}
            </DialogDescription>
          </DialogHeader>
          {active && (
            <>
              <div className="text-sm font-body bg-gray-50 border border-gray-200 rounded-sm p-3">
                <p className="font-bold mb-1">{active.user_name} — {active.user_email}</p>
                <p className="text-gray-700 whitespace-pre-wrap text-xs">{active.message}</p>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-700 font-body block mb-1">{t("appeals.notes_label")}</label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="font-body text-sm rounded-sm"
                  placeholder={t("appeals.notes_placeholder")}
                  maxLength={1000}
                  data-testid="appeal-decision-notes"
                />
              </div>
            </>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              onClick={() => decide("upheld")}
              disabled={busy}
              variant="outline"
              className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
              data-testid="appeal-uphold-btn"
            >
              <XCircle className="w-3.5 h-3.5 mr-1.5" /> {t("appeals.uphold_btn")}
            </Button>
            <Button
              onClick={() => decide("cleared")}
              disabled={busy}
              className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
              data-testid="appeal-clear-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {t("appeals.clear_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
